import React, { useEffect, useState } from 'react';
import { financeService } from './financeService';
import { PageHeader, MetricCard, SectionCard, LoadingState } from '../../components/common';
import { theme } from '../../design/theme';

export const PlatformAnalytics: React.FC = () => {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const data = await financeService.getPlatformAnalytics();
        // Add some mock data for the new requirements
        setMetrics({
          ...data,
          total_companies: 124,
          total_users: 15420,
          revenue_growth: 12.5,
          user_growth: 8.2
        });
      } catch (err) {
        console.error('Failed to fetch platform metrics', err);
        // Fallback mock data
        setMetrics({
          total_mrr: 1250000,
          active_companies: 124,
          total_tokens_consumed: 45000000,
          total_companies: 124,
          total_users: 15420,
          revenue_growth: 12.5,
          user_growth: 8.2
        });
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  if (loading) return <LoadingState />;
  if (!metrics) return <div>No metrics available</div>;

  const renderSimpleGraph = (color: string) => (
    <svg width="100%" height="60" viewBox="0 0 100 20" preserveAspectRatio="none">
      <path 
        d="M0 20 Q 25 5, 50 15 T 100 10" 
        fill="none" 
        stroke={color} 
        strokeWidth="2" 
      />
    </svg>
  );

  return (
    <div>
      <PageHeader 
        title="Platform Analytics" 
        subtitle="Global overview of financial health, company growth, and user engagement."
      />

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: theme.spacing.lg,
        marginBottom: theme.spacing.xl
      }}>
        <MetricCard 
          label="Annual Recurring Revenue" 
          value={`₹${(metrics.total_mrr * 12).toLocaleString()}`} 
          trend={{ value: metrics.revenue_growth, isPositive: true }}
          status="healthy"
          sparkline={renderSimpleGraph(theme.colors.healthy.main)}
        />
        <MetricCard 
          label="Total Companies" 
          value={metrics.total_companies} 
          trend={{ value: 4.2, isPositive: true }}
          status="info"
          sparkline={renderSimpleGraph(theme.colors.info.main)}
        />
        <MetricCard 
          label="Total Active Users" 
          value={metrics.total_users.toLocaleString()} 
          trend={{ value: metrics.user_growth, isPositive: true }}
          status="info"
          sparkline={renderSimpleGraph(theme.colors.brand.main)}
        />
        <MetricCard 
          label="Token Burn rate" 
          value={(metrics.total_tokens_consumed / 30 / 24).toFixed(0) + '/hr'} 
          status="warning"
          sparkline={renderSimpleGraph(theme.colors.warning.main)}
        />
      </div>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: theme.spacing.lg 
      }}>
        <SectionCard title="Revenue Distribution">
          <div style={{ height: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: theme.spacing.md }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={theme.typography.caption}>Subscription (75%)</span>
              <span style={theme.typography.caption}>₹{(metrics.total_mrr * 0.75).toLocaleString()}</span>
            </div>
            <div style={{ height: '12px', width: '100%', backgroundColor: 'var(--bg-muted)', borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: '75%', backgroundColor: theme.colors.brand.main }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={theme.typography.caption}>Token Overage (20%)</span>
              <span style={theme.typography.caption}>₹{(metrics.total_mrr * 0.20).toLocaleString()}</span>
            </div>
            <div style={{ height: '12px', width: '100%', backgroundColor: 'var(--bg-muted)', borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: '20%', backgroundColor: theme.colors.healthy.main }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={theme.typography.caption}>Custom Services (5%)</span>
              <span style={theme.typography.caption}>₹{(metrics.total_mrr * 0.05).toLocaleString()}</span>
            </div>
            <div style={{ height: '12px', width: '100%', backgroundColor: 'var(--bg-muted)', borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: '5%', backgroundColor: theme.colors.warning.main }} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Growth Trends">
          <div style={{ height: '300px', position: 'relative', display: 'flex', alignItems: 'flex-end', gap: '8px', padding: '20px 0' }}>
            {[40, 55, 48, 62, 75, 82, 90].map((h, i) => (
              <div key={i} style={{ flex: 1, backgroundColor: theme.colors.brand.main, height: `${h}%`, borderRadius: '4px 4px 0 0', opacity: 0.8 + (i * 0.03) }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
            <span style={theme.typography.caption}>Sep</span>
            <span style={theme.typography.caption}>Oct</span>
            <span style={theme.typography.caption}>Nov</span>
            <span style={theme.typography.caption}>Dec</span>
            <span style={theme.typography.caption}>Jan</span>
            <span style={theme.typography.caption}>Feb</span>
            <span style={theme.typography.caption}>Mar</span>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};
